/**
 * ProviderOSWorkDetail — one work item, loop-context first.
 *
 * The provider should never accept or work blind: this screen shows
 * read-only coverage status, the ACP/goals-of-care summary, and the
 * document signing pipeline with the in-platform sign action.
 * Fed by GET /api/provider/work/{id} + /api/provider/placements/{pid}/loop-context.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft, Loader2, ShieldCheck, ShieldAlert, HeartHandshake,
  FileText, CalendarClock, PenLine, Send, Info,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import ProviderOSGate from '../components/provider_os/ProviderOSGate';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOC_TONE = {
  draft: 'bg-slate-100 text-slate-700',
  out_for_sig: 'bg-blue-100 text-blue-800',
  signed: 'bg-emerald-100 text-emerald-800',
  exchanged: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-red-100 text-red-800',
};

export default function ProviderOSWorkDetail() {
  return (
    <ProviderOSGate>
      <DetailInner />
    </ProviderOSGate>
  );
}

function DetailInner() {
  const { workItemId } = useParams();
  const { token } = useAuth();
  const hdr = { headers: { Authorization: `Bearer ${token}` } };
  const [work, setWork] = useState(null);
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/provider/work/${workItemId}`, hdr);
      setWork(res.data);
      const pid = res.data?.work_item?.placement_id;
      if (pid) {
        try {
          const c = await axios.get(`${API}/provider/placements/${pid}/loop-context`, hdr);
          setCtx(c.data);
        } catch { setCtx(null); }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load work item');
    } finally { setLoading(false); }
  }, [workItemId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const routeForSig = async (docId) => {
    setBusy(docId);
    try {
      await axios.post(`${API}/provider/documents/${docId}/route-for-signature`, {}, hdr);
      toast.success('Routed for signature');
      await load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Could not route for signature');
    } finally { setBusy(null); }
  };

  const signDoc = async (docId, role) => {
    setBusy(docId);
    try {
      await axios.post(`${API}/provider/documents/${docId}/sign`,
        { role, method: 'esign' }, hdr);
      toast.success('Signature recorded');
      await load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Could not record signature');
    } finally { setBusy(null); }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading work item…
      </div>
    );
  }
  if (!work) {
    return (
      <div className="max-w-3xl mx-auto p-6" data-testid="work-detail-missing">
        <Link to="/provider-os/work" className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Back to work queue
        </Link>
        <p className="mt-6 text-slate-500">Work item not found or not yours.</p>
      </div>
    );
  }

  const wi = work.work_item || {};
  const coverage = ctx?.coverage;
  const acp = ctx?.acp;
  const docs = ctx?.documents?.length ? ctx.documents : (work.documents || []);

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="provider-work-detail">
      <Link to="/provider-os/work" className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> Back to work queue
      </Link>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            Placement <span className="font-mono text-lg text-slate-500">{(wi.placement_id || '').slice(0, 8)}…</span>
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Status: <span className="font-medium">{wi.status}</span>
            {work.first_visit_at && (
              <span className="ml-3 inline-flex items-center gap-1">
                <CalendarClock className="w-3.5 h-3.5" />
                First visit {new Date(work.first_visit_at).toLocaleString()}
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Coverage — read-only; the provider should never accept blind */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="work-coverage-panel">
        <div className="flex items-center gap-2 mb-3">
          {coverage?.passed ? (
            <ShieldCheck className="w-5 h-5 text-emerald-600" />
          ) : (
            <ShieldAlert className="w-5 h-5 text-amber-600" />
          )}
          <h2 className="text-base font-semibold text-slate-900">Coverage</h2>
          {coverage?.simulated && (
            <span className="text-[0.65rem] px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-slate-600"
              data-testid="coverage-simulated-badge">
              verification: simulated (pilot)
            </span>
          )}
        </div>
        {!coverage ? (
          <p className="text-sm text-slate-500">No coverage run recorded for this placement yet.</p>
        ) : (
          <div className="text-sm text-slate-700 space-y-1">
            <div>Eligibility: <span className="font-medium">{(coverage.eligibility_status || 'unknown').replace(/_/g, ' ')}</span></div>
            <div>Prior auth: <span className="font-medium">{(coverage.auth_status || 'unknown').replace(/_/g, ' ')}</span></div>
            <div className={coverage.passed ? 'text-emerald-700 font-medium' : 'text-amber-700 font-medium'}>
              {coverage.passed ? 'Coverage cleared before this placement routed to you.' : `On hold: ${(coverage.blocking_reasons || []).join('; ')}`}
            </div>
          </div>
        )}
      </div>

      {/* ACP / goals-of-care — read-only summary */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="work-acp-panel">
        <div className="flex items-center gap-2 mb-3">
          <HeartHandshake className="w-5 h-5 text-rose-600" />
          <h2 className="text-base font-semibold text-slate-900">Goals of care & directives</h2>
          <span className="text-[0.65rem] px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-slate-600">read-only</span>
        </div>
        {!acp?.entitled ? (
          <p className="text-sm text-slate-500">Advance-care-planning summary isn&apos;t enabled for this organization.</p>
        ) : !acp.summary ? (
          <p className="text-sm text-slate-500">No ACP record on file for this patient yet.</p>
        ) : (
          <div className="text-sm text-slate-700 space-y-2">
            {acp.summary.directives.length > 0 && (
              <div className="flex gap-2 flex-wrap">
                {acp.summary.directives.map((d, i) => (
                  <span key={i} className="text-xs px-2 py-1 rounded-full bg-rose-50 border border-rose-100 text-rose-800">
                    {(d.directive_type || '').replace(/_/g, ' ')} · {(d.status || '').replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            )}
            <div className="text-xs text-slate-500">
              {acp.summary.goc_conversations_count} goals-of-care conversation{acp.summary.goc_conversations_count === 1 ? '' : 's'} logged
              {acp.summary.goc_last_discussed_at && ` · last discussed ${acp.summary.goc_last_discussed_at.slice(0, 10)}`}
            </div>
            <div className="flex items-start gap-1.5 text-[0.7rem] text-slate-400">
              <Info className="w-3 h-3 shrink-0 mt-0.5" />
              Documentation only — not medical or legal advice.
            </div>
          </div>
        )}
      </div>

      {/* Document signing pipeline — the in-platform sign action */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="work-docs-panel">
        <div className="flex items-center gap-2 mb-3">
          <FileText className="w-5 h-5 text-slate-700" />
          <h2 className="text-base font-semibold text-slate-900">Orders & signatures</h2>
        </div>
        {docs.length === 0 ? (
          <p className="text-sm text-slate-500">No documents attached to this placement yet.</p>
        ) : (
          <div className="space-y-2">
            {docs.map((d) => {
              const unsigned = (d.signers || []).filter((s) => !s.signed_at);
              return (
                <div key={d.id} className="flex items-center justify-between gap-3 border border-slate-100 rounded-xl px-3 py-2.5 flex-wrap"
                  data-testid={`work-doc-${d.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{d.title || d.doc_type}</div>
                    <div className="text-xs text-slate-500">{d.doc_type}
                      {unsigned.length > 0 && d.status !== 'cancelled' && ` · awaiting: ${unsigned.map((s) => s.role).join(', ')}`}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${DOC_TONE[d.status] || DOC_TONE.draft}`}>
                      {(d.status || '').replace(/_/g, ' ')}
                    </span>
                    {d.status === 'draft' && (
                      <button onClick={() => routeForSig(d.id)} disabled={busy === d.id}
                        className="text-xs px-3 py-1.5 rounded-full bg-blue-600 text-white hover:bg-blue-700 font-medium inline-flex items-center gap-1"
                        data-testid={`route-sig-${d.id}`}>
                        <Send className="w-3 h-3" /> {busy === d.id ? '…' : 'Route for signature'}
                      </button>
                    )}
                    {d.status === 'out_for_sig' && unsigned.map((s) => (
                      <button key={s.role} onClick={() => signDoc(d.id, s.role)} disabled={busy === d.id}
                        className="text-xs px-3 py-1.5 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 font-medium inline-flex items-center gap-1"
                        data-testid={`sign-doc-${d.id}-${s.role}`}>
                        <PenLine className="w-3 h-3" /> {busy === d.id ? '…' : `Sign as ${s.role}`}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
